import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { RoutePath } from "@mirage/webapp/routeTypes";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useShouldShowExtensionOnboardingStep } from "../hooks/useShouldShowExtensionOnboardingStep";
import { ConnectorsOnboarding } from "./ConnectorsOnboarding";
import { ExtensionUpsell } from "./ExtensionUpsell";
import { useFinishOnboarding } from "./utils";

enum TeamOnboardingStep {
  EXTENSION = "browser-extension",
}

export const TeamOnboarding = ({
  routeParams: { step } = {},
  isDevboxBackend,
}: {
  routeParams?: Record<string, string | undefined>;
  isDevboxBackend: boolean;
}) => {
  const navigate = useNavigate();
  const { reportPapEvent } = useMirageAnalyticsContext();
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const fromDesktop = params.get("from") === "desktop";
  // secret url param to use to test against the dev desktop app
  const useDevDesktopApp = params.get("useDevDesktopApp") === "true";
  const finishOnboarding = useFinishOnboarding(reportPapEvent, "team_v1", true);
  const skipConnectorsOnboarding = convertFeatureValueToBool(
    useFeatureFlagValue("otc_growth_2025_05_06_skip_connectors_onboarding"),
  );
  const shouldShowExtensionUpsellStep = useShouldShowExtensionOnboardingStep();
  const handleStepFinish = useCallback(
    async (step: string) => {
      switch (step) {
        case TeamOnboardingStep.EXTENSION:
          navigate(getNavigatePathForStep(TeamOnboardingStep.EXTENSION));
          break;
        case "complete":
          await finishOnboarding(fromDesktop, useDevDesktopApp);
          break;
      }
    },
    [finishOnboarding, fromDesktop, navigate, useDevDesktopApp],
  );

  const getNavigatePathForStep = (step: TeamOnboardingStep): string => {
    return RoutePath.SETUP_TEAMS.replace(":step", step);
  };

  switch (step) {
    case TeamOnboardingStep.EXTENSION:
      return <ExtensionUpsell onStepFinish={handleStepFinish} />;
    default:
      // Skip connectors onboarding only if:
      // 1. The feature flag is enabled (skipConnectorsOnboarding)
      // 2. The user is eligible for extension upsell (shouldShowExtensionUpsellStep)
      // This ensures users always see at least one onboarding step, and allows us to punt the question of
      // how we should define "completed onboarding" to the future. If we decide to permanently remove the
      // connectors onboarding, we will need to revisit this.
      return skipConnectorsOnboarding && shouldShowExtensionUpsellStep ? (
        <ExtensionUpsell onStepFinish={handleStepFinish} />
      ) : (
        <ConnectorsOnboarding
          onStepFinish={handleStepFinish}
          isDevboxBackend={isDevboxBackend}
        />
      );
  }
};
